<template>
  <div class="rule-overview-view flex flex-col gap-5 py-5">
    <page-header>
      <template #buttons>
        <ui-button v-if="isLoading" disabled>
          <pencil-icon />
          {{ tr('Edit') }}
        </ui-button>
        <ui-button v-else as-child>
          <router-link :to="'/automation/' + rule.id + '/edit'">
            <pencil-icon />
            {{ tr('Edit') }}
          </router-link>
        </ui-button>

        <ui-dropdown-menu>
          <ui-dropdown-menu-trigger as-child>
            <ui-button
              variant="outline"
              size="icon"
              class="flex-none!"
              :disabled="isLoading"
              :aria-label="tr('More actions')"
            >
              <ellipsis-icon />
            </ui-button>
          </ui-dropdown-menu-trigger>
          <ui-dropdown-menu-content v-if="!isLoading" align="end" class="min-w-44">
            <ui-dropdown-menu-item as-child>
              <router-link :to="'/automation/' + rule.id + '/duplicate'">
                <copy-icon />
                {{ tr('Duplicate') }}
              </router-link>
            </ui-dropdown-menu-item>
            <ui-dropdown-menu-item
              v-if="canRunManually"
              :disabled="runIsDisabled"
              :title="runDisabledReason"
              @select="() => runRule()"
            >
              <loader-circle-icon v-if="isRunning" class="animate-spin" />
              <play-icon v-else />
              {{ tr('Run') }}
            </ui-dropdown-menu-item>
            <ui-dropdown-menu-separator />
            <ui-dropdown-menu-item variant="destructive" @select="() => openDeleteConfirmationModal()">
              <trash-2-icon />
              {{ tr('Delete') }}
            </ui-dropdown-menu-item>
          </ui-dropdown-menu-content>
        </ui-dropdown-menu>
      </template>
    </page-header>

    <asset-status-message
      :brand="rule?.brand"
      :ad-account="rule?.ad_account"
      :source-profiles="rule?.source_profiles ?? []"
    />

    <div class="grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <sticky-container :active="$root.screenWidth >= 1024" class="flex flex-col gap-5">
        <section class="glass-panel flex flex-col gap-4 rounded-xl p-5">
          <header class="flex items-center justify-between gap-3">
            <h2 class="m-0 flex items-center gap-2 text-sm font-semibold text-foreground">
              <span :class="[ 'size-2 rounded-full', rule?.active ? 'bg-success' : 'bg-muted-foreground/50' ]" />
              {{ tr('Automation') }}:
              <ui-skeleton v-if="isLoading" class="h-4 w-14" />
              <template v-else>{{ rule.active ? tr('Active') : tr('Inactive') }}</template>
            </h2>
            <switcher v-if="isLoading" disabled />
            <switcher
              v-else
              v-model="rule.active"
              v-tooltip="rule.isBlockedByInactiveBrand && !rule.active ? tr('The brand of this automation is inactive. Reactivate the brand to switch the automation on.') : null"
              :disabled="statusSwitchIsDisabled"
              :aria-label="tr('Active')"
              @update:model-value="(val) => onChangeRuleStatus(rule, val)"
            />
          </header>

          <rule-error-flags v-if="rule" :rule="rule" />

          <div v-if="isLoading" class="flex flex-col gap-3">
            <ui-skeleton class="h-4 w-2/5" />
            <ui-skeleton class="h-4 w-3/5" />
          </div>
          <dl v-else class="m-0 flex flex-col divide-y divide-border text-sm">
            <div
              v-for="profile in (rule.source_profiles ?? [])"
              :key="'source-profile--' + profile.id"
              class="flex items-center justify-between gap-3 pb-2.5"
            >
              <social-page :source-profile="profile" class="min-w-0 flex-1" />
              <div v-if="rule.action === 'start' || rule.action === 'scale'" class="flex flex-col items-end gap-1 text-xs text-foreground">
                <span class="flex items-center gap-1.5">
                  {{ tr('Ai Policy Check') }}
                  <shield-check-icon v-if="rule.settings.ai_review" class="size-4 text-primary" />
                  <shield-icon v-else class="size-4 text-muted-foreground" />
                </span>
                <span class="flex items-center gap-1.5">
                  {{ tr('Ai Content Actuality') }}
                  <calendar-check-icon v-if="rule.settings.dont_create_expired_ads" class="size-4 text-primary" />
                  <calendar-icon v-else class="size-4 text-muted-foreground" />
                </span>
              </div>
            </div>

            <div :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Brand') }}</dt>
              <dd :class="detailValueClasses">
                <router-link v-if="rule.brand" :to="'/brands/' + rule.brand.id" class="text-primary no-underline hover:underline">
                  {{ rule.brand.name }}
                </router-link>
                <template v-else>–</template>
              </dd>
            </div>

            <div v-if="rule.action !== 'stop'" :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Budget') }}</dt>
              <dd :class="[ detailValueClasses, 'flex items-center justify-end gap-1.5' ]">
                <ui-button
                  v-if="budget"
                  v-tooltip="tr('Edit budget')"
                  variant="ghost"
                  size="icon"
                  class="size-6 text-primary"
                  :aria-label="tr('Edit budget')"
                  @click="() => openBudgetModal()"
                >
                  <pencil-icon class="size-3.5" />
                </ui-button>
                {{ budget ?? '???' }}
              </dd>
            </div>

            <div v-if="rule.action !== 'stop'" :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Number of active ads') }}</dt>
              <dd :class="detailValueClasses">{{ rule.active_ads }}</dd>
            </div>
          </dl>
        </section>

        <section class="glass-panel flex flex-col gap-4 rounded-xl p-5">
          <h2 class="m-0 text-sm font-semibold text-foreground">
            {{ tr('Activity') }}
          </h2>

          <div v-if="isLoading" class="flex flex-col gap-3">
            <ui-skeleton class="h-4 w-2/5" />
            <ui-skeleton class="h-4 w-3/5" />
            <ui-skeleton class="h-4 w-1/3" />
          </div>
          <dl v-else class="m-0 flex flex-col divide-y divide-border text-sm">
            <div :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Created') }}</dt>
              <dd :class="detailValueClasses">{{ formatToStringYMD(rule.created_at, tr) }}</dd>
            </div>

            <div :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Last modified') }}</dt>
              <dd :class="detailValueClasses">
                <span v-if="rule.updated_at" v-tooltip="formatToYMD(rule.updated_at) + '. ' + formatTime(rule.updated_at)">
                  {{ timeAgo(rule.updated_at, tr) }}
                </span>
                <template v-else>–</template>
              </dd>
            </div>

            <div :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Last run') }}</dt>
              <dd :class="detailValueClasses">
                <span v-if="rule.last_run" v-tooltip="formatToYMD(rule.last_run) + '. ' + formatTime(rule.last_run)">
                  {{ timeAgo(rule.last_run, tr) }}
                </span>
                <template v-else>–</template>
              </dd>
            </div>

            <div v-if="rule.active" :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Next run in') }}</dt>
              <dd :class="[ detailValueClasses, 'tabular-nums' ]">
                <span v-if="rule.is_running" class="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <loader-circle-icon class="size-3.5 animate-spin" />
                  {{ tr('Running now...') }}
                </span>
                <template v-else>{{ nextRunCountdown }}</template>
              </dd>
            </div>

            <div :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Last activity') }}</dt>
              <dd :class="detailValueClasses">
                <span v-if="rule.last_activity" v-tooltip="formatToYMD(rule.last_activity) + '. ' + formatTime(rule.last_activity)">
                  {{ timeAgo(rule.last_activity, tr) }}
                </span>
                <template v-else>–</template>
              </dd>
            </div>

            <div :class="detailRowClasses">
              <dt :class="detailLabelClasses">{{ tr('Tasks done') }}</dt>
              <dd :class="detailValueClasses">{{ rule.triggered > 0 ? rule.triggered : '–' }}</dd>
            </div>
          </dl>
        </section>

        <rule-links-card
          :title="tr('Connected automations')"
          :rules="rule?.connected_rules ?? []"
          :loading="isLoading"
          :empty-text="tr('No connected automations')"
        />
      </sticky-container>

      <content-panel :title="tr('Activities')" :icon="HistoryIcon" class="min-w-0">
        <activity-log
          ref="activityLog"
          :rule-id="rule ? rule.id : null"
          :is-running="shouldShowRunningIndicatorInActivityLog"
          :skeleton="isLoading"
        />
      </content-panel>
    </div>
  </div>
</template>

<script>
import {
  Calendar,
  CalendarCheck,
  Copy,
  Ellipsis,
  History,
  LoaderCircle,
  Pencil,
  Play,
  Shield,
  ShieldCheck,
  Trash2,
} from '@lucide/vue';
import { markRaw } from 'vue';

import Rule from '@/entities/Rule.js';
import AssetStatusMixin from '@/mixins/AssetStatus.js';
import RulePollingMixin from '@/mixins/RulePolling.js';
import RuleSwitchMixin from '@/mixins/RuleSwitch.js';
import RuleService from '@/services/Rule.js';
import ActivityLog from '@/ui/components/ActivityLog.vue';
import AssetStatusMessage from '@/ui/components/AssetStatusMessage.vue';
import ContentPanel from '@/ui/components/ContentPanel.vue';
import DeleteRuleConfirmModalContent from '@/ui/components/DeleteRuleConfirmModalContent.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import RuleBudgetEditModalContent from '@/ui/components/RuleBudgetEditModalContent.vue';
import RuleErrorFlags from '@/ui/components/RuleErrorFlags.vue';
import RuleLinksCard from '@/ui/components/RuleLinksCard.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import StickyContainer from '@/ui/components/StickyContainer.vue';
import Switcher from '@/ui/components/Switcher.vue';
import { formatTime, formatToStringYMD, formatToYMD, timeAgo } from '@/utils/date';
import { formatNumber } from '@/utils/number';

export default {
  name: 'RuleOverviewView',
  components: {
    ActivityLog,
    AssetStatusMessage,
    CalendarCheckIcon: CalendarCheck,
    CalendarIcon: Calendar,
    ContentPanel,
    CopyIcon: Copy,
    EllipsisIcon: Ellipsis,
    LoaderCircleIcon: LoaderCircle,
    PageHeader,
    PencilIcon: Pencil,
    PlayIcon: Play,
    RuleErrorFlags,
    RuleLinksCard,
    ShieldCheckIcon: ShieldCheck,
    ShieldIcon: Shield,
    SocialPage,
    StickyContainer,
    Switcher,
    Trash2Icon: Trash2,
  },
  mixins: [ AssetStatusMixin, RulePollingMixin, RuleSwitchMixin ],
  inject: [ 'ruleView' ],
  data() {
    return {
      detailRowClasses: 'flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0',
      detailLabelClasses: 'shrink-0 text-muted-foreground',
      detailValueClasses: 'm-0 min-w-0 text-right font-medium text-foreground',
      nextRunCountdown: '',
      countdownTimer: null,
      initValueOfTriggered: 0,
      HistoryIcon: markRaw(History),
    };
  },
  computed: {
    rule() {
      return this.ruleView.rule;
    },
    isLoading() {
      return !(this.rule instanceof Rule);
    },
    canRunManually() {
      return this.$root.currentCompany.enabled_manual_run && [ 'admin', 'owner' ].includes(this.$root.loggedInUser.level);
    },
    isRunning() {
      return this.rule?.is_running || false;
    },
    runIsDisabled() {
      return this.isRunning || this.rule?.isBlockedByExpiredToken === true;
    },
    runDisabledReason() {
      if (this.rule?.isBlockedByExpiredToken !== true) {
        return null;
      }

      return this.expiredConnectionReason(this.rule.expiredTokenConnectedProfile);
    },
    statusSwitchIsDisabled() {
      return this.$root.loggedInUser.isRestrictedViewer
        || this.$root.isSupportMode
        || this.isLoading
        || this.deletedCampaignFlagIsVisible
        || this.rule.hasLostAccessAssets
        || (this.rule.isBlockedByInactiveBrand && !this.rule.active);
    },
    deletedCampaignFlagIsVisible() {
      return this.rule.campaign_status === 'deleted';
    },
    budget() {
      let budget = null;
      let period = null;
      let isAdsetBudget = false;

      if (this.rule.output_campaign && this.rule.output_campaign.budget) {
        budget = formatNumber(this.rule.output_campaign.budget, null, 2);
        period = this.rule.output_campaign.budget_period;
      }
      else if (this.rule.settings.adset_budget) {
        budget = formatNumber(this.rule.settings.adset_budget, null, 2);
        period = this.rule.settings.adset_budget_period;
        isAdsetBudget = true;
      }
      else {
        return null;
      }

      const currency = this.rule.adAccountCurrency;
      if (!currency) {
        return null;
      }

      let resultString = '';

      if (this.$root.language === 'hu') {
        resultString = `${budget} ${currency.symbol} ${this.tr(period)}`;
      }
      else {
        resultString = `${currency.symbol}${budget} ${this.tr(period)}`;
      }

      if (isAdsetBudget) {
        resultString += ` / ${this.tr('Ad set')}`;
      }

      return resultString;
    },
    shouldShowRunningIndicatorInActivityLog() {
      return (this.rule?.isPostBoosting || this.rule?.isScaling)
        && this.rule?.is_running
        && this.initValueOfTriggered === 0;
    },
  },
  watch: {
    'rule.active': {
      immediate: true,
      handler(val, oldVal) {
        if (val) {
          if (val !== oldVal) {
            this.initCountdownTimer();
          }
        }
        else {
          this.destroyCountdownTimer();
        }
      },
    },
    'rule.id': {
      immediate: true,
      handler(id) {
        if (id) {
          this.observeRule(this.rule);
          this.initValueOfTriggered = this.rule.triggered;
        }
      },
    },
    'rule.last_activity': {
      handler(newVal, oldVal) {
        if (newVal?.toString() !== oldVal?.toString() && this.$refs.activityLog) {
          this.$refs.activityLog.refresh();
        }
      },
    },
    'rule.hasLostAccessAssets': {
      immediate: true,
      handler(val) {
        if (this.rule && val > 0 && this.rule.active) {
          this.rule.active = false;
        }
      },
    },
  },
  beforeUnmount() {
    this.destroyCountdownTimer();
  },
  methods: {
    formatToStringYMD,
    formatToYMD,
    formatTime,
    timeAgo,
    initCountdownTimer() {
      this.destroyCountdownTimer();
      this.updateNextRunCountdown();
      this.countdownTimer = setInterval(this.updateNextRunCountdown, 1000);
    },
    destroyCountdownTimer() {
      if (this.countdownTimer !== null) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        this.nextRunCountdown = '–';
      }
    },
    updateNextRunCountdown() {
      if (!this.rule) {
        this.nextRunCountdown = '–';
        return;
      }

      const { nextRunInSeconds } = this.rule;
      if (nextRunInSeconds === null) {
        this.nextRunCountdown = '–';
      }
      else {
        this.nextRunCountdown = this.secToHHMMSS(nextRunInSeconds);
      }
    },
    secToHHMMSS(secNum) {
      let hours = Math.floor(secNum / 3600);
      let minutes = Math.floor((secNum - hours * 3600) / 60);
      let seconds = Math.floor(secNum - hours * 3600 - minutes * 60);
      if (hours < 10) {
        hours = '0' + hours;
      }
      if (minutes < 10) {
        minutes = '0' + minutes;
      }
      if (seconds < 10) {
        seconds = '0' + seconds;
      }
      let result = minutes + ':' + seconds;
      if (hours !== '00') {
        result = hours + ':' + result;
      }
      return result;
    },
    openDeleteConfirmationModal() {
      this.$fullscreenModal.open(
        this,
        DeleteRuleConfirmModalContent,
        { rule: this.rule },
      );
    },
    openBudgetModal() {
      this.$fullscreenModal.open(
        this,
        RuleBudgetEditModalContent,
        { rule: this.rule },
      );
    },
    runRule() {
      if (this.runIsDisabled) {
        return;
      }

      this.$toast.clear();

      RuleService.run(this.rule.id)
        .then(resp => {
          this.rule.updateWith(resp);
          this.$store.commit('rules/updateInList', this.rule);

          this.$toast.success(this.tr('Automation started successfully. It is now running and may take some time.'));

          return resp;
        })
        .catch(err => this.error(err));
    },
  },
};
</script>
